import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WaitlistService, LandingService } from "@/lib/db";
import { SEO } from './SEO';
import { ExtensionConfirmationDialog } from "./ExtensionConfirmationDialog";
import {
    Loader2,
    CheckCircle2,
    Menu,
    X,
    Puzzle,
    PlayCircle,
    BarChart3,
    Zap,
    ShieldCheck,
    MousePointer2,
    FileText,
    ArrowRight,
    Check,
    ListTodo,
    Timer,
    CheckCircle
} from "lucide-react";

interface LandingPageProps {
    onLogin: () => void;
    onDemo: () => void;
    onPrivacy: () => void;
    onGetStarted: () => void;
    onViewAllBlogs: () => void;
    onReadBlog: (id: string) => void;
    onSupport: () => void;
}

export function LandingPage({ onLogin, onDemo, onPrivacy, onGetStarted, onViewAllBlogs, onReadBlog, onSupport }: LandingPageProps) {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const [blogs, setBlogs] = useState<any[]>([]);
    const [pipeline, setPipeline] = useState<any[]>([]);
    const [pricingTiers, setPricingTiers] = useState<any[]>([]);
    const [extensionConfig, setExtensionConfig] = useState<any>(null);
    const [showExtensionConfirm, setShowExtensionConfirm] = useState(false);
    const [isExtensionActive, setIsExtensionActive] = useState(false);

    useEffect(() => {
        // detection
        const checkExtension = () => {
            if ((window as any).traskrExtensionActive || document.body.getAttribute('data-traskr-extension-active') === 'true') {
                setIsExtensionActive(true);
            }
        };

        checkExtension();
        window.addEventListener('traskr-extension-ready', checkExtension);
        const interval = setInterval(checkExtension, 1000);

        return () => {
            window.removeEventListener('traskr-extension-ready', checkExtension);
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const content = await LandingService.getContent();
                if (content.pricingTiers?.length > 0) setPricingTiers(content.pricingTiers);
                if (content.pipeline?.length > 0) setPipeline(content.pipeline);
                if (content.extensionConfig) setExtensionConfig(content.extensionConfig);

                const blogRes = await LandingService.getBlogs();
                setBlogs(blogRes.blogs || []);
            } catch (e) {
                console.error("Failed to fetch landing content:", e);
                // ... (rest of the existing fallback logic)
                // Fallback pricing if empty/error
                setPricingTiers([
                    {
                        name: "Early Adopter",
                        price: "0",
                        period: "forever",
                        description: "Perfect for those just starting their search.",
                        features: ["Up to 50 applications", "Chrome Extension access", "Standard Sankey diagrams", "Cloud Resume Storage"],
                        buttonText: "Join Waitlist",
                        isComingSoon: false,
                        isPro: false
                    },
                    {
                        name: "Pro Career",
                        price: "12",
                        period: "month",
                        description: "For serious hunters who want the AI edge.",
                        features: ["Infinite applications", "Advanced AI Match Scoring", "Custom Data Exports", "Priority Support", "Personalized Insights"],
                        buttonText: "Coming Soon",
                        isComingSoon: true,
                        isPro: true
                    }
                ]);
            }
        };
        fetchContent();
    }, []);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        setMobileMenuOpen(false);
    };

    const handleExtensionClick = () => {
        if (isExtensionActive) return;
        if (extensionConfig?.enabled && (extensionConfig.downloadUrl || extensionConfig.zipUrl)) {
            setShowExtensionConfirm(true);
        } else {
            onGetStarted();
        }
    };

    const handleJoinWaitlist = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        setError(null);
        try {
            await WaitlistService.joinWaitlist(email);
            setSuccess(true);
            setEmail("");
        } catch (err: any) {
            console.error("Waitlist error:", err);
            setError("Failed to join. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="min-h-screen bg-background flex flex-col">
            <SEO />
            <header className="border-b sticky top-0 bg-background/80 backdrop-blur-md z-[60]">
                <div className="container flex h-16 items-center px-4 max-w-7xl mx-auto">
                    {/* Left: Logo */}
                    <div className="flex items-center gap-3 cursor-pointer shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                        <img src="/logo.svg" alt="Traskr Logo" className="h-8 w-8" />
                        <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Traskr</span>
                    </div>

                    {/* Center: Desktop Nav */}
                    <nav className="hidden md:flex flex-1 justify-center items-center gap-8 text-sm font-medium text-muted-foreground">
                        <button onClick={() => scrollToSection('features')} className="hover:text-blue-600 transition-colors">Features</button>
                        <button onClick={() => scrollToSection('pricing')} className="hover:text-blue-600 transition-colors">Pricing</button>
                        <button onClick={() => scrollToSection('about')} className="hover:text-blue-600 transition-colors">About</button>
                        <button onClick={() => scrollToSection('blogs')} className="hover:text-blue-600 transition-colors">Blogs</button>
                    </nav>

                    {/* Right: Auth Buttons & Mobile Toggle */}
                    <div className="flex items-center gap-4 shrink-0 ml-auto md:ml-0">
                        <div className="hidden md:flex items-center gap-4">
                            <Button variant="ghost" onClick={onLogin}>Sign In</Button>
                            <Button onClick={onGetStarted}>Get Started</Button>
                        </div>

                        {/* Mobile Menu Toggle */}
                        <button
                            className="md:hidden p-2 text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                {mobileMenuOpen && (
                    <div className="md:hidden absolute top-16 left-0 w-full bg-background border-b shadow-xl animate-in slide-in-from-top-2 duration-200 z-[55]">
                        <nav className="flex flex-col p-6 space-y-4 items-center text-center">
                            <button onClick={() => scrollToSection('features')} className="w-full py-3 font-medium border-b border-border/50 hover:text-blue-600 transition-colors">Features</button>
                            <button onClick={() => scrollToSection('pricing')} className="w-full py-3 font-medium border-b border-border/50 hover:text-blue-600 transition-colors">Pricing</button>
                            <button onClick={() => scrollToSection('about')} className="text-center w-full py-3 font-medium border-b border-border/50 hover:text-blue-600 transition-colors">About</button>
                            <button onClick={() => scrollToSection('blogs')} className="text-center w-full py-3 font-medium border-b border-border/50 hover:text-blue-600 transition-colors">Blogs</button>
                            <div className="flex flex-col gap-3 pt-4 w-full max-w-sm">
                                <Button variant="outline" className="w-full py-6 text-base" onClick={() => { onLogin(); setMobileMenuOpen(false); }}>Sign In</Button>
                                <Button className="w-full py-6 text-base" onClick={() => { onGetStarted(); setMobileMenuOpen(false); }}>
                                    Get Started
                                </Button>
                            </div>
                        </nav>
                    </div>
                )}
            </header>

            <main className="flex-1">
                {/* Hero Section */}
                <section className="py-12 md:py-32 text-center space-y-8 container px-4 mx-auto max-w-4xl animate-in fade-in slide-in-from-bottom-5 duration-1000">
                    <div className="space-y-4">
                        <h1 className="text-4xl md:text-7xl font-extrabold tracking-tight leading-tight">
                            Supercharge Your <br className="hidden md:block" />
                            <span className="text-blue-600 relative inline-block">
                                Job Search
                                <span className="absolute -bottom-2 left-0 w-full h-1.5 bg-blue-100 -z-10 rounded-full hidden md:block" />
                            </span>
                        </h1>
                        <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            Track applications, visualize your progress, and master your career hunt with Traskr.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                        <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 rounded-xl shadow-xl shadow-blue-100 flex items-center gap-2" onClick={handleExtensionClick}>
                            {isExtensionActive ? <Puzzle className="w-5 h-5" /> : <Puzzle className="w-5 h-5" />}
                            {isExtensionActive ? "Extension Installed" : "Add to Chrome - It's Free"}
                        </Button>
                        <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 rounded-xl border-2 flex items-center gap-2" onClick={onDemo}>
                            <PlayCircle className="w-5 h-5" />
                            View Demo
                        </Button>
                    </div>

                    {/* Product Hunt Embed */}
                    <div className="pt-8 flex justify-center">
                        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', border: '1px solid rgb(224, 224, 224)', borderRadius: '12px', padding: '20px', maxWidth: '500px', background: 'rgb(255, 255, 255)', boxShadow: 'rgba(0, 0, 0, 0.05) 0px 2px 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <img alt="Traskr" src="https://ph-files.imgix.net/a5b450e0-65b8-4f63-a831-fd548bb9cf09.png?auto=format&fit=crop&w=80&h=80" style={{ width: '64px', height: '64px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                                <div style={{ flex: '1 1 0%', minWidth: '0px', textAlign: 'left' }}>
                                    <h3 style={{ margin: '0px', fontSize: '18px', fontWeight: 600, color: 'rgb(26, 26, 26)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Traskr</h3>
                                    <p style={{ margin: '4px 0px 0px', fontSize: '14px', color: 'rgb(102, 102, 102)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>Clip, track, and manage your job applications in one click.</p>
                                </div>
                            </div>
                            <a href="https://www.producthunt.com/products/traskr?embed=true&utm_source=embed&utm_medium=post_embed" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '12px', padding: '8px 16px', background: 'rgb(255, 97, 84)', color: 'rgb(255, 255, 255)', textDecoration: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600 }}>Check it out on Product Hunt →</a>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section id="features" className="py-24 bg-muted/30 border-y">
                    <div className="container px-4 mx-auto max-w-7xl">
                        <div className="text-center space-y-4 mb-20">
                            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">Everything you need to land the offer.</h2>
                            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                                We've automated the tedious parts of the job search so you can focus on what matters.
                            </p>
                        </div>

                        {/* Main Features with Images */}
                        <div className="space-y-32">
                            {[
                                {
                                    icon: <Puzzle className="w-8 h-8 text-blue-600" />,
                                    title: "Smart Job Clipping",
                                    description: "Save any job post from LinkedIn, Indeed, or Glassdoor with a single click using our Chrome extension. No more manual copy-pasting.",
                                    image: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&q=80&w=800",
                                    badge: "Best Seller",
                                    bullets: [
                                        "One-click data extraction",
                                        "Works on all major boards",
                                        "Seamless dashboard sync"
                                    ]
                                },
                                {
                                    icon: <BarChart3 className="w-8 h-8 text-purple-600" />,
                                    title: "Visual Pipeline",
                                    description: "Understand your success rate with beautiful Sankey diagrams. See exactly where your applications are dropping off and optimize your strategy.",
                                    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800",
                                    badge: "Data Focused",
                                    bullets: [
                                        "Sankey Visuals",
                                        "Realtime Update",
                                        "Analysis First"
                                    ]
                                }
                            ].map((feature, index) => (
                                <div key={index} className={`flex flex-col md:flex-row items-center gap-8 md:gap-12 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                                    <div className="flex-1 space-y-6 text-left">
                                        <div className="inline-block px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wider">
                                            {feature.badge}
                                        </div>
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 rounded-2xl bg-white shadow-md border">
                                                    {feature.icon}
                                                </div>
                                                <h2 className="text-3xl font-bold text-slate-900">{feature.title}</h2>
                                            </div>
                                            <p className="text-lg text-slate-600 leading-relaxed">
                                                {feature.description}
                                            </p>
                                        </div>
                                        <ul className="space-y-3">
                                            {feature.bullets.map((item: any, i: any) => (
                                                <li key={i} className="flex items-center gap-2 text-sm text-slate-600 font-medium">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600" /> {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="flex-1 w-full max-w-xl">
                                        <div className="relative group">
                                            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-1000"></div>
                                            <img
                                                src={feature.image}
                                                alt={feature.title}
                                                loading="lazy"
                                                className="relative rounded-2xl shadow-2xl border w-full h-[400px] object-cover"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Unmatched Capabilities (Dark Mode) */}
                <section className="py-24 bg-slate-900 text-white">
                    <div className="container px-4 mx-auto max-w-7xl text-center space-y-16">
                        <div className="space-y-4">
                            <h2 className="text-3xl md:text-5xl font-bold">Unmatched Capabilities</h2>
                            <p className="text-slate-400 max-w-2xl mx-auto">
                                We've packed Traskr with features that help you stay one step ahead of the competition.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                {
                                    icon: <Zap className="w-6 h-6 text-amber-500" />,
                                    title: "AI Match Score",
                                    description: "Get instant feedback on how well your resume matches a job description before you even apply."
                                },
                                {
                                    icon: <ShieldCheck className="w-6 h-6 text-green-500" />,
                                    title: "Secure Cloud Storage",
                                    description: "Keep all your resumes and job descriptions in one secure, encrypted location accessible anywhere."
                                },
                                {
                                    icon: <MousePointer2 className="w-6 h-6 text-indigo-500" />,
                                    title: "One-Click Tracking",
                                    description: "Track status updates, interview dates, and salary ranges with an intuitive drag-and-drop interface."
                                },
                                {
                                    icon: <FileText className="w-6 h-6 text-rose-500" />,
                                    title: "Resume Management",
                                    description: "Manage multiple versions of your resume and track which one works best for specific roles."
                                }
                            ].map((cap, index) => (
                                <div key={index} className="bg-white/5 border border-white/10 p-8 rounded-3xl text-left hover:bg-white/10 transition-colors">
                                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
                                        {cap.icon}
                                    </div>
                                    <h3 className="text-xl font-bold mb-3">{cap.title}</h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">{cap.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Upcoming Features & Pipeline Section */}
                <section id="pipeline" className="py-24 bg-background border-b overflow-hidden">
                    <div className="container px-4 mx-auto max-w-7xl">
                        <div className="text-center space-y-4 mb-16">
                            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">Feature Pipeline</h2>
                            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                                See what we're building next. Our roadmap is driven by your feedback.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {pipeline.length === 0 ? (
                                <div className="col-span-full p-12 border-2 border-dashed rounded-3xl text-center space-y-4 bg-muted/30">
                                    <div className="p-4 bg-muted rounded-full w-fit mx-auto">
                                        <ListTodo className="w-8 h-8 text-muted-foreground" />
                                    </div>
                                    <h3 className="text-xl font-bold">Roadmap Coming Soon</h3>
                                    <p className="text-muted-foreground">We're busy planning the next generation of Traskr. Check back soon for updates!</p>
                                </div>
                            ) : (
                                pipeline.map((item, i) => (
                                    <div key={i} className="p-8 border rounded-3xl bg-card/50 shadow-sm space-y-4">
                                        <div className="flex items-center gap-3">
                                            {item.status === 'in-progress' ? <Timer className="w-5 h-5 text-amber-500" /> :
                                                item.status === 'completed' ? <CheckCircle className="w-5 h-5 text-green-500" /> :
                                                    <ListTodo className="w-5 h-5 text-blue-500" />}
                                            <span className={`text-xs font-bold uppercase tracking-widest ${item.status === 'in-progress' ? 'text-amber-600' :
                                                item.status === 'completed' ? 'text-green-600' : 'text-blue-600'
                                                }`}>
                                                {item.status === 'in-progress' ? 'In Progress' : item.status === 'completed' ? 'Completed' : 'Planned'}
                                            </span>
                                        </div>
                                        <h3 className="text-xl font-bold">{item.title}</h3>
                                        <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </section>

                {/* Pricing Section */}
                <section id="pricing" className="py-24 container px-4 mx-auto max-w-7xl">
                    <div className="text-center space-y-4 mb-20">
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight">Simple, transparent pricing.</h2>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            Choose the plan that's right for your career stage.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {pricingTiers.length === 0 ? (
                            <div className="col-span-full p-12 border-2 border-dashed rounded-3xl text-center space-y-4">
                                <div className="p-4 bg-muted rounded-full w-fit mx-auto">
                                    <Timer className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <h3 className="text-xl font-bold">Pricing Plans Coming Soon</h3>
                                <p className="text-muted-foreground">We're finalizing our pricing models. Stay tuned for early adopter rewards!</p>
                            </div>
                        ) : (
                            pricingTiers.map((tier, i) => (
                                <div key={i} className={`p-8 rounded-3xl space-y-8 relative shadow-2xl transition-all hover:scale-[1.02] ${tier.isPro
                                    ? "bg-blue-600 text-white shadow-blue-200"
                                    : "bg-background border-2 shadow-slate-100"
                                    }`}>
                                    {tier.tag && (
                                        <div className={`absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${tier.isPro ? "bg-blue-900 text-white" : "bg-blue-100 text-blue-700"
                                            }`}>
                                            {tier.tag}
                                        </div>
                                    )}
                                    <div className="space-y-4">
                                        <h3 className="text-2xl font-bold text-left">{tier.name}</h3>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-bold">${tier.price}</span>
                                            <span className={`${tier.isPro ? 'text-blue-100' : 'text-muted-foreground'}`}>/ {tier.period}</span>
                                        </div>
                                        <p className={`${tier.isPro ? 'text-blue-100' : 'text-muted-foreground'} text-left`}>
                                            {tier.description}
                                        </p>
                                    </div>
                                    <ul className="space-y-4">
                                        {tier.features.map((feature: string, j: number) => (
                                            <li key={j} className="flex items-center gap-2">
                                                <Check className={`w-5 h-5 ${tier.isPro ? 'text-blue-200' : 'text-green-600'}`} />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="space-y-3">
                                        <Button
                                            className={`w-full py-6 text-lg rounded-xl transition-colors ${tier.isPro
                                                ? (tier.isComingSoon ? "bg-white text-blue-600 hover:bg-white/90" : "bg-white text-blue-600 hover:bg-blue-50")
                                                : "shadow-blue-100"
                                                }`}
                                            variant={tier.isPro ? "default" : "outline"}
                                            onClick={() => tier.isComingSoon ? scrollToSection('waitlist') : onGetStarted()}
                                        >
                                            {tier.buttonText}
                                        </Button>
                                        {tier.isComingSoon && (
                                            <p className={`text-center text-xs font-medium ${tier.isPro ? 'text-blue-100' : 'text-muted-foreground'}`}>
                                                * We will inform creators of interest as soon as this is available.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                {/* About Section */}
                <section id="about" className="py-24 bg-slate-100/50 border-t overflow-hidden relative">
                    <div className="container px-4 mx-auto max-w-5xl space-y-16">
                        <div className="text-center space-y-4">
                            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">Our Mission</h2>
                            <p className="text-slate-600 text-xl leading-relaxed max-w-3xl mx-auto">
                                Traskr was born from frustration. After juggling endless spreadsheets and losing track of half-finished applications, we built the tool we wished we had.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div className="bg-white p-10 rounded-4xl shadow-sm border space-y-4">
                                <h3 className="text-2xl font-bold text-blue-600">Built for Speed</h3>
                                <p className="text-slate-600 leading-relaxed text-lg">We believe your time is better spent preparing for the interview than entering data into a database. Our extension extracts the info so you don't have to.</p>
                            </div>
                            <div className="bg-white p-10 rounded-4xl shadow-sm border space-y-4">
                                <h3 className="text-2xl font-bold text-indigo-600">Data Empowered</h3>
                                <p className="text-slate-600 leading-relaxed text-lg">Visualizing your progress isn't just about looks—it's about understanding your funnel, identifying bottlenecks, and landing the job faster.</p>
                            </div>
                        </div>
                        <div className="text-center pt-8">
                            <Button size="lg" className="rounded-full px-10 py-7 text-xl shadow-xl shadow-blue-100" onClick={onGetStarted}>
                                Join 2,000+ Job Seekers <ArrowRight className="ml-2 w-6 h-6" />
                            </Button>
                        </div>
                    </div >
                </section >

                <section id="waitlist" className="py-24 bg-muted/50 border-t">
                    <div className="container max-w-md mx-auto px-4 text-center space-y-6">
                        <h2 className="text-3xl font-bold tracking-tight">Join the Waiting List</h2>
                        <p className="text-muted-foreground text-lg">Be the first to know when we launch new features.</p>

                        {success ? (
                            <div className="bg-green-50 border border-green-200 p-6 rounded-2xl text-green-700 flex flex-col items-center gap-4 animate-in fade-in zoom-in duration-500">
                                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                                    <CheckCircle2 className="w-6 h-6" />
                                </div>
                                <div className="space-y-1">
                                    <p className="font-bold text-lg text-green-900">You're on the list!</p>
                                    <p className="text-green-700">Check your inbox for a confirmation soon.</p>
                                </div>
                            </div>
                        ) : (
                            <form className="flex gap-2 p-1 bg-background border rounded-2xl shadow-sm focus-within:ring-2 focus-within:ring-blue-600 transition-all" onSubmit={handleJoinWaitlist}>
                                <Input
                                    type="email"
                                    placeholder="Enter your email"
                                    className="border-none bg-transparent focus-visible:ring-0 py-6 text-lg"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                />
                                <Button type="submit" disabled={loading} className="py-6 px-8 rounded-xl shadow-lg shadow-blue-100">
                                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Join Now"}
                                </Button>
                            </form>
                        )}
                        {error && <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1">{error}</p>}
                    </div>
                </section>

                {/* Blogs Section */}
                <section id="blogs" className="py-24 bg-white border-t">
                    <div className="container px-4 mx-auto max-w-7xl">
                        <div className="text-center space-y-4 mb-16">
                            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">Latest from the Blog</h2>
                            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                                Insights, tips, and tricks to help you master your job search.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {blogs.length === 0 ? (
                                <div className="col-span-full p-12 border-2 border-dashed rounded-3xl text-center space-y-4">
                                    <div className="p-4 bg-muted rounded-full w-fit mx-auto">
                                        <FileText className="w-8 h-8 text-muted-foreground" />
                                    </div>
                                    <h3 className="text-xl font-bold">Blog Coming Soon</h3>
                                    <p className="text-muted-foreground">Stories and insights from the Traskr team are on their way!</p>
                                </div>
                            ) : (
                                blogs
                                    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
                                    .slice(0, 6)
                                    .map((blog, i) => (
                                        <div
                                            key={i}
                                            className="group cursor-pointer p-0 border rounded-3xl bg-slate-50 flex flex-col transition-all hover:shadow-lg overflow-hidden"
                                            onClick={() => onReadBlog(blog.id)}
                                        >
                                            <div className="aspect-video bg-muted relative overflow-hidden">
                                                {blog.coverImage ? (
                                                    <img
                                                        src={blog.coverImage}
                                                        alt={blog.title}
                                                        loading="lazy"
                                                        className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                                                        <FileText className="w-12 h-12 text-slate-400" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-8 flex-1 flex flex-col space-y-4">
                                                <div className="space-y-2 flex-1">
                                                    <h3 className="text-xl font-bold group-hover:text-blue-600 transition-colors line-clamp-2">{blog.title}</h3>
                                                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">{blog.excerpt}</p>
                                                </div>
                                                <div className="pt-4 border-t flex justify-between items-center text-xs font-medium text-slate-400">
                                                    <span>By {blog.author}</span>
                                                    <span>{new Date(blog.publishedAt).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                            )}
                        </div>

                        {blogs.length > 0 && (
                            <div className="text-center mt-12">
                                <Button variant="outline" size="lg" className="rounded-full px-8" onClick={onViewAllBlogs}>
                                    View All Articles <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                </section>
            </main>

            <footer className="border-t py-6">
                <div className="container mx-auto flex flex-col items-center gap-2 text-sm text-muted-foreground px-4">
                    <div>&copy; {new Date().getFullYear()} Traskr.</div>
                    <div className="flex gap-4">
                        <button onClick={onPrivacy} className="hover:underline">Privacy Policy</button>
                        <button onClick={onSupport} className="hover:underline">Support</button>
                    </div>
                </div>
            </footer>
            <ExtensionConfirmationDialog
                open={showExtensionConfirm}
                onOpenChange={setShowExtensionConfirm}
                config={extensionConfig}
            />
        </div>
    );
}
